import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, ShieldIcon, ActivityIcon, ThermometerIcon } from 'lucide-react';
function MetricPill({
  label,
  value,
  color = 'purple'
}: {
  label: string;
  value: string;
  color?: string;
}) {
  const colorMap: Record<string, string> = {
    purple: 'text-tiq-purple border-tiq-purple/20 bg-tiq-purple/5',
    green: 'text-tiq-green border-tiq-green/20 bg-tiq-green/5',
    blue: 'text-tiq-blue border-tiq-blue/20 bg-tiq-blue/5'
  };
  return <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${colorMap[color]}`} data-id="element-1">
      <span className="text-[10px] font-mono uppercase tracking-wider opacity-70" data-id="element-2">
        {label}
      </span>
      <span className="text-sm font-mono font-bold" data-id="element-3">{value}</span>
    </div>;
}
function ScoreTransition({
  from,
  to,
  delay = 0
}: {
  from: string;
  to: string;
  delay?: number;
}) {
  return <motion.div className="flex items-center gap-2" initial={{
    opacity: 0
  }} whileInView={{
    opacity: 1
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.5
  }} data-id="element-4">
      <span className="text-2xl font-mono text-slate-500 font-bold" data-id="element-5">
        {from}
      </span>
      <ArrowRightIcon className="w-4 h-4 text-tiq-green" data-id="element-6" />
      <span className="text-2xl font-mono text-tiq-green font-bold" data-id="element-7">{to}</span>
    </motion.div>;
}
export function CaseStudiesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="w-full bg-slate-950 py-24 lg:py-32 relative" data-id="element-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9">
        {/* Header */}
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-4" data-id="element-11">
            Readiness Intelligence in Action
          </h2>
          <p className="text-lg text-slate-400" data-id="element-12">
            Validated across mission-critical environments.
          </p>
        </motion.div>

        {/* Case Study Cards */}
        <div className="grid lg:grid-cols-3 gap-6" data-id="element-13">
          {/* Card 1 — CAF Navy Crew */}
          <motion.article className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden hover:border-tiq-purple/30 transition-colors duration-300" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-14">
            <div className="h-1 bg-gradient-to-r from-tiq-purple to-tiq-dark" data-id="element-15" />
            <div className="p-6 lg:p-8" data-id="element-16">
              <div className="flex items-center gap-2 mb-4" data-id="element-17">
                <ShieldIcon className="w-4 h-4 text-tiq-purple" data-id="element-18" />
                <span className="text-[10px] font-mono text-tiq-purple uppercase tracking-wider" data-id="element-19">
                  CAF Navy Crew
                </span>
              </div>

              <h3 className="text-xl font-bold text-white mb-2" data-id="element-20">
                Operational Consistency
              </h3>
              <p className="text-sm font-mono text-slate-500 mb-6" data-id="element-21">
                Skill Consistency & Performance Check
              </p>

              <p className="text-slate-300 text-sm leading-relaxed mb-6" data-id="element-22">
                During a routine CAF audit, the Mission Engine detected
                inconsistency in Level 3 knot-tying certifications. It flagged
                performance variance and training fatigue.
              </p>

              <ScoreTransition from="0.63" to="0.88" delay={0.5} data-id="element-23" />

              <div className="flex flex-wrap gap-2 mt-6 mb-6" data-id="element-24">
                <MetricPill label="Consistency" value="+37%" color="green" data-id="element-25" />
                <MetricPill label="Time" value="10 Days" color="blue" data-id="element-26" />
              </div>

              <div className="pt-4 border-t border-slate-800" data-id="element-27">
                <p className="text-sm font-semibold text-tiq-green" data-id="element-28">
                  Boosted execution consistency
                </p>
              </div>
            </div>
          </motion.article>

          {/* Card 2 — Arctic Deployment */}
          <motion.article className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden hover:border-tiq-purple/30 transition-colors duration-300" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.35
        }} data-id="element-29">
            <div className="h-1 bg-gradient-to-r from-tiq-green to-emerald-700" data-id="element-30" />
            <div className="p-6 lg:p-8" data-id="element-31">
              <div className="flex items-center gap-2 mb-4" data-id="element-32">
                <ActivityIcon className="w-4 h-4 text-tiq-green" data-id="element-33" />
                <span className="text-[10px] font-mono text-tiq-green uppercase tracking-wider" data-id="element-34">
                  Active Service Commander
                </span>
              </div>

              <h3 className="text-xl font-bold text-white mb-2" data-id="element-35">
                Army Deployment Readiness
              </h3>
              <p className="text-sm font-mono text-slate-500 mb-6" data-id="element-36">
                120-Person Arctic Mission
              </p>

              <p className="text-slate-300 text-sm leading-relaxed mb-6" data-id="element-37">
                The Mission Engine synchronized HR and training data, surfacing
                a 28% certification gap in cold-weather engineering and
                forecasting a 25% delay risk.
              </p>

              <ScoreTransition from="0.71" to="0.90" delay={0.65} data-id="element-38" />

              <div className="flex flex-wrap gap-2 mt-6 mb-6" data-id="element-39">
                <MetricPill label="Training" value="5 Days" color="green" data-id="element-40" />
                <MetricPill label="Status" value="On Time" color="blue" data-id="element-41" />
              </div>

              <div className="pt-4 border-t border-slate-800" data-id="element-42">
                <p className="text-sm font-semibold text-tiq-green" data-id="element-43">
                  Deployment proceeds on time
                </p>
              </div>
            </div>
          </motion.article>

          {/* Card 3 — Polar Operations */}
          <motion.article className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden hover:border-tiq-purple/30 transition-colors duration-300" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.5
        }} data-id="element-44">
            <div className="h-1 bg-gradient-to-r from-tiq-blue to-cyan-600" data-id="element-45" />
            <div className="p-6 lg:p-8" data-id="element-46">
              <div className="flex items-center gap-2 mb-4" data-id="element-47">
                <ThermometerIcon className="w-4 h-4 text-tiq-blue" data-id="element-48" />
                <span className="text-[10px] font-mono text-tiq-blue uppercase tracking-wider" data-id="element-49">
                  Polar Bear Pilot
                </span>
              </div>

              <h3 className="text-xl font-bold text-white mb-2" data-id="element-50">
                Extreme Climate Adaptability
              </h3>
              <p className="text-sm font-mono text-slate-500 mb-6" data-id="element-51">
                Fatigue Risk Prevention
              </p>

              <p className="text-slate-300 text-sm leading-relaxed mb-6" data-id="element-52">
                Integrated environmental sensors and health telemetry analyzed
                resilience under -40°C conditions, revealing fatigue risks among
                night-shift officers.
              </p>

              <div className="mb-6" data-id="element-53">
                <div className="flex items-baseline gap-1" data-id="element-54">
                  <span className="text-2xl font-mono text-tiq-green font-bold" data-id="element-55">
                    +22%
                  </span>
                  <span className="text-sm font-mono text-slate-400" data-id="element-56">
                    continuity
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-6" data-id="element-57">
                <MetricPill label="Drop" value="18%" color="purple" data-id="element-58" />
                <MetricPill label="Horizon" value="60 Days" color="blue" data-id="element-59" />
              </div>

              <div className="pt-4 border-t border-slate-800" data-id="element-60">
                <p className="text-sm font-semibold text-tiq-green" data-id="element-61">
                  Improved operational continuity
                </p>
              </div>
            </div>
          </motion.article>
        </div>
      </div>
    </section>;
}